import React, { useState, useEffect } from 'react';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const VideoGenerationConfigEditor = () => {
    const [config, setConfig] = useState({
        serviceProvider: '',
        serviceProviderUrl: '',
        model: 'default',
        duration: -1,  // Changed to -1 for default
        loop: false,
        aspect_ratio: 'default',  // Changed to default
        resolution: 'default'  // Changed to default
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Configuration options with default as first option
    const modelOptions = ['default', 'ray-1-6', 'ray-2', 'ray-flash-2'];
    const durationOptions = [-1, 5, 9];
    const aspectRatioOptions = ['default', '1:1', '16:9', '9:16', '4:3', '3:4', '21:9', '9:21'];
    const resolutionOptions = ['default', '540p', '720p', '1080p', '4k'];

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/api/admin/video-config');
            setConfig(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to load configuration: ' + (err.response?.data?.error || err.message));
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            await api.post('/api/admin/video-config', config);
            setSuccess('Video generation configuration updated successfully');
        } catch (err) {
            setError('Failed to update configuration: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setConfig({
            ...config,
            [e.target.name]: value
        });
    };

    if (loading && !config) {
        return (
            <div className={`${theme.card.base} mb-8`}>
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${theme.card.base}`}>
            <h2 className="text-xl font-semibold text-[#0C3B2E] mb-6">Video Generation Configuration</h2>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                    <p>{error}</p>
                </div>
            )}

            {success && (
                <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
                    <p>{success}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Service Provider
                    </label>
                    <input
                        type="text"
                        name="serviceProvider"
                        value={config.serviceProvider}
                        onChange={handleChange}
                        className={theme.input.base}
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Service Provider URL
                    </label>
                    <input
                        type="url"
                        name="serviceProviderUrl"
                        value={config.serviceProviderUrl}
                        onChange={handleChange}
                        className={theme.input.base}
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Model
                    </label>
                    <select
                        name="model"
                        value={config.model}
                        onChange={handleChange}
                        className={theme.input.base}
                        required
                    >
                        {modelOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Duration (seconds)
                    </label>
                    <select
                        name="duration"
                        value={config.duration}
                        onChange={e => handleChange({
                            target: {
                                name: 'duration',
                                value: parseInt(e.target.value)
                            }
                        })}
                        className={theme.input.base}
                        required
                    >
                        {durationOptions.map(option => (
                            <option key={option} value={option}>
                                {option === -1 ? 'Default' : `${option} seconds`}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Aspect Ratio
                    </label>
                    <select
                        name="aspect_ratio"
                        value={config.aspect_ratio}
                        onChange={handleChange}
                        className={theme.input.base}
                        required
                    >
                        {aspectRatioOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#0C3B2E] mb-2">
                        Resolution
                    </label>
                    <select
                        name="resolution"
                        value={config.resolution}
                        onChange={handleChange}
                        className={theme.input.base}
                        required
                    >
                        {resolutionOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="loop"
                        checked={config.loop}
                        onChange={handleChange}
                        className="h-4 w-4 text-[#6D9773] focus:ring-[#6D9773] border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-[#0C3B2E]">
                        Loop Video
                    </label>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </div>
                        ) : 'Save Configuration'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VideoGenerationConfigEditor;